import Modal from "react-bootstrap/Modal";
import { useEffect, useState } from "react";
import Select from "react-select";
import { getPractitionerList, getPractitionerAuthorizedList, getSingleUser } from "../../api/Individual";
import { failed, success } from "../../common/Toastify";
import { Button } from "../../common/Button";
import { useSelector } from "react-redux";
import { updateTask } from "../../api/Task";
import moment from "moment";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { Table } from "react-bootstrap";
import { TableCell } from "@mui/material";
import "./TaskStyles.css";

const AssignPractitionerModal = (props) => {
  const onHide = props.onHide;
  const onShow = props.onShow;
  const data = props.data;
  const organizations = props.organizations;
  const [practitionerList, setPractitionerList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const practitionerId = useSelector(
    (state) => state?.auth?.user?.["custom:unique_id"]
  );
  const practUserName = useSelector(
    (state) => state?.auth?.user?.name[0]?.text
  );
  const [myUserId, setMyUserId] = useState("");
  const practitionerHistory = data?.practitionerHistory;
  const [page, setPage] = useState(0);
  const rowsPerPage = 3;
  const reversedPractitionerHistory = [...practitionerHistory]?.reverse();
  const totalRows = reversedPractitionerHistory?.length;
  const userOrganizationId = useSelector(
    (state) => state?.auth?.user?.["organizations"][0].id
  );
  const [orgId, setOrgId] = useState(userOrganizationId)


  const capitalizeWords = (str) => {
    return str
      .toLowerCase()
      .replace(/(^|\s)\S/g, (firstLetter) => firstLetter.toUpperCase());
  };

  const handleOrgChange = (event) => {
      let value = event?.value;
      setOrgId(value);
      setSelectedPractitioner(null);
    };

  useEffect(() => {
    getPractitionerAuthorizedList((orgId || data?.orgId), data?.patientLocation)
      .then((res) => {
        let unsorted = res.result.filter(item => item.name || false);
        setPractitionerList(unsorted);
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => setIsLoading(false));
  }, [orgId]);

  useEffect(() => {
    getSingleUser({ id: practitionerId, type: "Practitioner" })
      .then((res) => {
        setMyUserId(res?.data?.link?.[0]?.target?.reference?.split("/")?.[1]);
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      });
  }, []);

  const practitionerOptions = practitionerList.map((practitioner) => ({
    value: practitioner.name?.[0]?.text,
    label: capitalizeWords(practitioner.name?.[0]?.text),
    data: practitioner,
  })).sort((a, b) => a.label.localeCompare(b.label));

  const currentPractitioner =
    reversedPractitionerHistory?.[0]?.actor?.display || null;

  const handlePractitionerChange = (selectedOption) => {
    setSelectedPractitioner(selectedOption);
  };

  /**
 * Assign the selected practitioner to the Task
 * — adds a new practitionerHistory entry
 * — sets owner / ownerRef / ownerDisplay at the same time
 */
const handleSubmit = async () => {
    // turn the spinner on immediately
    setIsLoading(true);
  
    if (!selectedPractitioner) {
      failed("Please select a practitioner from the list.");
      setIsLoading(false);
      return;
    }
  
    try {
      const currentPractitionerHistory = data?.practitionerHistory ?? [];
  
      /* build the history entry */
      const newEntry = {
        actor: {
          display: selectedPractitioner.label,                         // pretty name
          reference: `Practitioner/${selectedPractitioner.data.id}`,
          type: "Practitioner",
        },
        assignedBy: {
          actor: {
            display: practUserName,
            reference: `Practitioner/${myUserId}`,
            type: "Practitioner",
          },
        },
        updatedAt: Date.now(),
      };
  
      const updatedPractitionerHistory = [
        ...currentPractitionerHistory,
        newEntry,
      ];
  
      /* persist to the Task table */
      await updateTask({
        id: data.id,
        owner: {
          reference: `Practitioner/${selectedPractitioner.data.id}`,
          display: selectedPractitioner.label,
          type: "Practitioner",
        },
        practitionerHistory: updatedPractitionerHistory,
        orgId: data.orgId
      });
  
      success(
        `Practitioner assigned successfully for ${capitalizeWords(
          data?.patient?.display ?? ""
        )}`
      );
      onHide();
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
    } finally {
      setIsLoading(false);          // always executed
    }
  };
  
  /**
   * Un-assign the current practitioner from the Task
   * — pushes a “blank” practitionerHistory entry
   * — clears owner / ownerRef / ownerDisplay
   */
  const handleUnAssign = async () => {
    setIsLoading(true);
  
    try {
      const currentPractitionerHistory = data?.practitionerHistory ?? [];
  
      const newEntry = {
        actor: { display: "-", reference: "-", type: "-" },
        assignedBy: {
          actor: {
            display: practUserName,
            reference: `Practitioner/${myUserId}`,
            type: "Practitioner",
          },
        },
        updatedAt: Date.now(),
      };
  
      const updatedPractitionerHistory = [
        ...currentPractitionerHistory,
        newEntry,
      ];
  
      await updateTask(
        {
          id: data.id,
          owner: null,                       // ⟵ clears owner / ownerRef / ownerDisplay server-side
          practitionerHistory: updatedPractitionerHistory,
          orgId: data.orgId
        },
      );
  
      success(
        `Practitioner unassigned successfully for ${capitalizeWords(
          data?.patient?.display ?? ""
        )}`
      );
      onHide();
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <Modal
      backdropClassName
      backdrop={"static"}
      show={onShow}
      onHide={onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="custom-dialog"
    >
      <Modal.Header closeButton className="border-0">
        <span className="header-text">Practitioner History:</span>
        &nbsp;&nbsp;
        <Modal.Title
          id="contained-modal-title-vcenter"
          style={{ textTransform: "capitalize" }}
        >
          {data?.subject?.display}
        </Modal.Title>
        &nbsp;&nbsp;&nbsp;&nbsp;
      </Modal.Header>
      <Modal.Body>
        {!currentPractitioner || currentPractitioner === "-" ? (
          <div className="select-submit-container">
            <div className="select-container">
              <span>Select Organization:</span>
              <Select
                onChange={handleOrgChange}
                isMulti={false}
                name="organizationId"
                options={organizations}
                className="search-select"
                classNamePrefix="select"
                isClearable={true}
                autoFocus
                isLoading={organizations?.length >= 1 ? false : true}
              />
            </div>
            <div className="select-container">
              <span>Select Practitioner:</span>
              <Select
                onChange={handlePractitionerChange}
                isMulti={false}
                name="practitionerId"
                options={practitionerOptions}
                className="search-select"
                classNamePrefix="select"
                value={selectedPractitioner}
                isClearable={true}
                autoFocus
                isLoading={practitionerList?.length > 0 ? false : true}
              />
            </div>
            <div className="btn-wrap-assign">
              <Button
                type="submit"
                onClick={handleSubmit}
                isLoading={isLoading}
              >
                Assign
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <span>Assigned Practitioner:</span>&nbsp;&nbsp;
              <span style={{ fontWeight: "bold", color: "#015ba8" }}>
                {capitalizeWords(currentPractitioner)}
              </span>
            </div>
            <div className="btn-wrap-unassign">
              <Button
                title="Unassign"
                onClick={handleUnAssign}
                isLoading={isLoading}
              >
                Unassign
              </Button>
            </div>
          </div>
        )}

        <div className="table-wrap">
          <Table responsive>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Assigned Practitioner</th>
                <th>Assigned By</th>
              </tr>
            </thead>
            <tbody>
              {reversedPractitionerHistory?.length > 0 ? (
                reversedPractitionerHistory
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map((data, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          {moment(data?.updatedAt).format("DD-MMM-YY HH:mm")}
                        </td>{" "}
                        <td style={{ textTransform: "capitalize" }}>
                          {data?.actor?.display}
                        </td>
                        <td style={{ textTransform: "capitalize" }}>
                          {data?.assignedBy?.actor?.display}
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <TableCell colSpan={"8"} style={{ textAlign: "center" }}>
                    No Data Available
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
          {reversedPractitionerHistory?.length > 0 ? (
            <CustomPagination
              tableData={reversedPractitionerHistory}
              totalRows={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              handlePage={(pageNo) => setPage(pageNo)}
            />
          ) : null}
        </div>

        <div className="btn-wrap">
          <Button onClick={() => onHide()} variant="secondary" title="Cancel">
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AssignPractitionerModal;
